import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";

export interface PaymentFooterPrimary {
  label: string;
  /** Associate with a form's id so a submit button can live outside the form. */
  formId?: string;
  type?: "button" | "submit";
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  color?: "primary" | "success";
  startIcon?: React.ReactNode;
}

interface PaymentFooterProps {
  primary?: PaymentFooterPrimary;
  cancelLabel?: string;
  onCancel?: () => void;
  cancelDisabled?: boolean;
}

/**
 * Shared action footer for the payment step. Hosts the primary action (e.g.
 * "Pay now" for cards) and a single shared Cancel, so individual payment
 * methods don't render their own footer buttons.
 */
export const PaymentFooter: React.FC<PaymentFooterProps> = ({
  primary,
  cancelLabel = "Cancel",
  onCancel,
  cancelDisabled,
}) => {
  if (!primary && !onCancel) return null;

  return (
    <Box
      sx={{
        mt: 3,
        pt: 2,
        borderTop: "1px solid",
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        maxWidth: 420,
        mx: "auto",
      }}
    >
      {primary && (
        <Button
          variant="contained"
          color={primary.color ?? "primary"}
          fullWidth
          type={primary.type ?? "button"}
          form={primary.formId}
          onClick={primary.onClick}
          disabled={primary.disabled || primary.loading}
          startIcon={
            primary.loading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              primary.startIcon
            )
          }
        >
          {primary.label}
        </Button>
      )}

      {onCancel && (
        <Button
          variant="text"
          fullWidth
          size="small"
          onClick={onCancel}
          disabled={cancelDisabled}
        >
          {cancelLabel}
        </Button>
      )}
    </Box>
  );
};

export default PaymentFooter;
